/**
 * CaptionControl - Component for rendering captions in tile-footer slot
 *
 * This component extracts and renders captions from media metadata.
 * It's used as a control in the tile-footer slot to display captions below the media.
 * Uses a DropdownMenu to show the full caption when expanded.
 */

import {IconButton, useTruncate} from '@wandb/components';
import {linkify} from '@wandb/weave/common/util/links';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React, {useRef, useState} from 'react';
import {useClickAway} from 'react-use';

import {useIsTextOverflowing} from '../../../util/hooks/useIsTextOverflowing';
import {getRawCaptionsFromMetadata} from '../../../util/media';
import {useDetectPanelVariant} from '../../Panel/hooks/hooks';
import {
  type LayoutConfigContextValue,
  useLayoutConfig,
} from '../layout/contexts/LayoutConfigContext';
import {type GridCell} from '../layout/types';
import {type CaptionControlProps} from './types';

export type {CaptionControlProps};

/**
 * Gathers caption data from a cell.
 * Returns empty strings if no caption is found (to avoid null checks).
 */
function getCaptionData(
  layoutConfig: LayoutConfigContextValue | undefined | null,
  cell: GridCell | undefined | null
): {
  fullCaptionText: string;
  truncatedCaption: string;
} {
  const EMPTY_RESULT = {
    fullCaptionText: '',
    truncatedCaption: '',
  };

  if (!layoutConfig || !cell || cell.isEmpty) {
    return EMPTY_RESULT;
  }

  const {run, mediaKey, tileSpec} = cell;
  const {
    mediaStep: step,
    mediaIndex,
    mediaSliderKey,
    mediaSliderKeyValue,
  } = tileSpec;

  // Find the history row: try slider key match first, fallback to step match
  const useSliderKey =
    mediaSliderKeyValue != null && mediaSliderKey && mediaSliderKey !== '_step';
  const historyRow =
    (useSliderKey
      ? run.history?.find(
          hr => hr[mediaKey] && hr[mediaSliderKey] === mediaSliderKeyValue
        )
      : undefined) ??
    run.history?.find(hr => hr[mediaKey] && hr._step === step);

  // Extract and process captions
  const metadata = historyRow?.[mediaKey];
  if (!metadata) {
    return EMPTY_RESULT;
  }

  const rawCaptions = getRawCaptionsFromMetadata(metadata, mediaIndex)
    .map(caption => String(caption).trim())
    .filter(Boolean);

  if (rawCaptions.length === 0) {
    return EMPTY_RESULT;
  }

  return {
    fullCaptionText: rawCaptions.join('\n\n'),
    truncatedCaption: rawCaptions.map(c => c.replace(/\n/g, ' ')).join(' • '),
  };
}

/**
 * Expanded caption dropdown content
 */
const ExpandedCaptionDropdown: React.FC<{
  fullCaptionText: string;
  copySuccess: boolean;
  onCopy: () => void;
  onClose: () => void;
}> = ({fullCaptionText, copySuccess, onCopy, onClose}) => (
  <Tailwind>
    {/* z-[2] is needed to be above the videojs player's progress slider https://github.com/videojs/video.js/blob/main/src/css/components/_progress.scss#L99 */}
    <div className="absolute bottom-0 left-0 right-0 rounded-t-lg border border-wb-border bg-wb-surface shadow-medium p-16 flex flex-col gap-8 z-[2]">
      <div className="flex gap-8 items-start justify-between">
        <div className="flex-1 text-sm text-left text-wb-text-secondary">
          Caption
        </div>
        <div className="flex gap-4">
          <IconButton
            variant="ghost"
            size="sm"
            icon={copySuccess ? 'checkmark-circle' : 'copy'}
            onPress={() => {
              onCopy();
            }}
            aria-label="Copy caption"
          />
          <IconButton
            variant="ghost"
            size="sm"
            icon="expand-down"
            onPress={() => {
              onClose();
            }}
            aria-label="Minimize caption"
          />
        </div>
      </div>
      <div className="text-xs text-left whitespace-pre-wrap break-words max-h-[200px] overflow-y-auto">
        {linkify(fullCaptionText, {
          onClick: (e: React.MouseEvent) => e.stopPropagation(),
        })}
      </div>
    </div>
  </Tailwind>
);

/**
 * Interior component that handles caption display with hooks.
 * Hooks can be called unconditionally here since we know we have a caption.
 */
const CaptionDisplay: React.FC<{
  fullCaptionText: string;
  truncatedCaption: string;
  isFullScreenPanelPage: boolean;
}> = ({fullCaptionText, truncatedCaption, isFullScreenPanelPage}) => {
  const [copySuccess, setCopySuccess] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const isTextOverflowing = useIsTextOverflowing({
    text: truncatedCaption,
    parentDomRef: containerRef,
  });

  const result = useTruncate(truncatedCaption, {
    ref: containerRef,
  });

  // Close dropdown when clicking outside both trigger and content
  // useClickAway(triggerRef, () => setIsOpen(false));
  useClickAway(containerRef, () => setIsOpen(false));

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullCaptionText);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy caption:', err);
    }
  };

  // In fullscreen mode, show the expandable dropdown if caption is long
  if (isFullScreenPanelPage && isTextOverflowing) {
    // Caption is long, show expandable dropdown
    return (
      <div
        ref={containerRef}
        className="image-card-caption relative w-full min-h-[24px]">
        <div ref={triggerRef} className="flex items-center gap-4 min-w-0 px-6">
          <span className="flex-1 truncate text-xs min-w-0">
            {linkify(truncatedCaption, {
              onClick: (e: React.MouseEvent) => e.stopPropagation(),
            })}
          </span>
          <IconButton
            variant="ghost"
            size="sm"
            icon="expand-up"
            onPress={() => setIsOpen(!isOpen)}
            aria-label="Expand caption"
          />
        </div>
        {isOpen && (
          <ExpandedCaptionDropdown
            fullCaptionText={fullCaptionText}
            copySuccess={copySuccess}
            onCopy={handleCopy}
            onClose={() => setIsOpen(false)}
          />
        )}
      </div>
    );
  }

  // In workspace mode, show simple tooltip with preserved whitespace
  return (
    <div
      className="image-card-caption min-h-[24px] items-center text-xs"
      ref={containerRef}>
      <Tooltip
        position="top center"
        content={
          <pre className="text-xs break-words m-0 p-0 font-sans">
            {linkify(fullCaptionText, {
              onClick: (e: React.MouseEvent) => e.stopPropagation(),
            })}
          </pre>
        }
        trigger={
          <div>
            {linkify(result.text, {
              onClick: (e: React.MouseEvent) => e.stopPropagation(),
            })}
          </div>
        }
      />
    </div>
  );
};

/**
 * Renders captions for a media tile.
 *
 * @param props - CaptionControlProps
 * @returns Rendered captions or null if no captions
 */
export const CaptionControl: React.FC<CaptionControlProps> = ({
  cell: inputCell,
  columnIndex,
  rowIndex,
}) => {
  const layoutConfig = useLayoutConfig();
  const {isFullScreenPanelPage} = useDetectPanelVariant();

  // Resolve the cell if not provided
  let cell: GridCell | undefined = inputCell;
  if (!inputCell) {
    if (columnIndex != null && layoutConfig) {
      // allow for grabbing the first row, we expect only 1 row in the main use case.
      cell = layoutConfig.getCell(columnIndex, rowIndex ?? 0);
    }
  }

  // Gather caption data (returns empty strings if no caption found)
  const {fullCaptionText, truncatedCaption} = getCaptionData(
    layoutConfig,
    cell
  );

  // Render the caption display with hooks
  return (
    <CaptionDisplay
      fullCaptionText={fullCaptionText}
      truncatedCaption={truncatedCaption}
      isFullScreenPanelPage={!!isFullScreenPanelPage}
    />
  );
};
